import { FormEvent, useState } from "react";
import { ApiError } from "../../api/client";
import {
  INSTRUCTOR_LEAVE_OPTIONS,
  Instructor,
  InstructorLeaveType,
  createInstructorLeave,
} from "../../api/instructors";
import "./InstructorLeaveModal.css";

type Props = {
  instructor: Instructor;
  onClose: () => void;
  onSaved: () => void;
};

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function InstructorLeaveModal({ instructor, onClose, onSaved }: Props) {
  const initialDate = todayIso();
  const [startDate, setStartDate] = useState(initialDate);
  const [endDate, setEndDate] = useState(initialDate);
  const [leaveType, setLeaveType] = useState<InstructorLeaveType | "">("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    if (!leaveType) {
      setError("İzin türü seçiniz.");
      return;
    }
    if (endDate < startDate) {
      setError("Bitiş tarihi başlangıçtan önce olamaz.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await createInstructorLeave(instructor.id, {
        startDate,
        endDate,
        leaveType,
        note: note.trim() || null,
      });
      if (result.days_created === 0) {
        setError("Seçilen aralıkta (bugünden itibaren) izin kaydı oluşturulamadı.");
        return;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "İzin kaydedilemedi.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="kp-modal kp-card kp-instructor-leave">
        <div className="kp-instructor-leave__title-wrap">
          <h2 className="kp-instructor-leave__title">İzin Ekle</h2>
          <p className="kp-instructor-leave__instructor">{instructor.full_name.toLocaleUpperCase("tr-TR")}</p>
          <p className="kp-instructor-leave__subtitle">
            Belirli tarih aralığı için tek seferlik izin. Her hafta tekrarlayan izin günü için{" "}
            <strong>Düzenle</strong> menüsünü kullanın.
          </p>
        </div>

        <form className="kp-instructor-leave__form" onSubmit={submit}>
          <div className="kp-instructor-leave__dates">
            <label className="kp-instructor-leave__field">
              <span>Başlangıç Tarihi</span>
              <input
                className="kp-input"
                type="date"
                required
                min={initialDate}
                value={startDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v);
                  if (endDate < v) setEndDate(v);
                }}
              />
            </label>
            <label className="kp-instructor-leave__field">
              <span>Bitiş Tarihi</span>
              <input
                className="kp-input"
                type="date"
                required
                min={startDate < initialDate ? initialDate : startDate}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
          </div>

          <label className="kp-instructor-leave__field kp-instructor-leave__field--full">
            <span>İzin Türü</span>
            <select
              className="kp-input"
              required
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value as InstructorLeaveType | "")}
            >
              <option value="">-- Seçiniz --</option>
              {INSTRUCTOR_LEAVE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="kp-instructor-leave__field kp-instructor-leave__field--full">
            <span>Not (isteğe bağlı)</span>
            <textarea
              className="kp-input"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          {error && <div className="kp-instructor-leave__error">{error}</div>}

          <div className="kp-instructor-leave__actions">
            <button type="button" className="kp-btn" onClick={onClose} disabled={submitting}>
              İptal
            </button>
            <button type="submit" className="kp-btn kp-btn--primary" disabled={submitting}>
              {submitting ? "Kaydediliyor…" : "İzni Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
